/* eslint-disable default-case */
import { useTranslation } from 'react-i18next'
import React, { useState, useEffect } from 'react'

/** Material UI **/
import { HighlightButton } from '../../styles/mui_custom_components'
import { LockOutlined } from '@mui/icons-material'
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material'
import { forgotPasswordCodeStyles } from '../../styles/classes/LogInClasses'

/** Components **/
import LogInContainer from '../../components/LogInContainer'
import { LoadingSplash } from '../../components/LoadingSplash'

/** Validations **/
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

/** Redux **/
import { useDispatch } from 'react-redux'
import { loadingActions } from '../../store/loading'

/** Services **/
import { resetPassword, login } from '../../services/AuthService'

const ForgotPasswordCode = () => {
  const { t } = useTranslation()
  const { classes } = forgotPasswordCodeStyles()
  const dispatch = useDispatch()
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState()
  const [loading, setLoading] = useState(false)
  const [queryParams, setQueryParams] = useState(null)

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    setQueryParams(queryParams)
  }, [])

  /** VALIDATIONS **/
  const validationSchema = yup.object().shape({
    code: yup.string().required(t('general.messages.errors.required')),
    new_password: yup
      .string()
      .required(t('general.messages.errors.required'))
      .test(
        'len',
        t('forgot_password_code.min_6_chars'),
        (val) => val.toString().length >= 6
      )
  })

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema)
  })

  const onSubmit = async () => {
    try {
      setLoading(true)
      dispatch(loadingActions.show())
      const selector = queryParams.get('selector')
      const token = queryParams.get('token')
      const apiToken = queryParams.get('apiToken')
      const username = queryParams.get('u')
      await resetPassword(selector, token, newPassword, newPassword, apiToken)
      await login(username, newPassword)
      dispatch(loadingActions.hide())
    } catch (error) {
      console.error(error)
      switch (error.message) {
        case 'Invalid Password':
          setError(t('forgot_password_code.invalid_password'))
          break
        case 'Invalid Token':
          setError(t('forgot_password_code.invalid_token'))
          break
      }
      dispatch(loadingActions.hide())
      setLoading(false)
    }
  }

  const handleNewPasswordChange = (event) => {
    const value = event.target.value.replace(/\s/g, '')
    if (value.length < 6) {
      setError(t('forgot_password_code.min_6_chars'))
    } else {
      setError(null)
    }
    setNewPassword(value)
  }

  if (loading) return <LoadingSplash />

  const content = <Grid
  data-testid={'forgot_password_code_page'}
  className={classes.mainGrid}
  container
  spacing={0}
  direction="column"
  alignItems="center"
>
  <Grid className={classes.mainItem} item xs={12}>
    <Grid container justifyContent="center" alignItems="center">
      <Grid item xs={12} textAlign="center">
        <Typography align="center" className={classes.title} variant="p">
          {t('forgot_password_code.title').toUpperCase()}
        </Typography>
      </Grid>
      <Grid item xs={12} mt={3}>
        <Typography align="center" className={classes.subtitle}>
          {t('forgot_password_code.subtitle')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <Box className={classes.formBox}>
          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <TextField
              className={classes.fields}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              type="password"
              id="new_password"
              placeholder={t('forgot_password_code.new_password')}
              autoComplete="off"
              name="new_password"
              inputProps={{
                minLength: 6
              }}
              error={!!errors.new_password}
              helperText={
                errors.new_password && errors.new_password.message
              }
              {...register('new_password')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined />
                  </InputAdornment>
                ),
                classes: {
                  notchedOutline: classes.fieldsOutlined
                }
              }}
              onChange={handleNewPasswordChange}
              value={newPassword}
            />
            <Box className={classes.errorBox}>
              <Typography align={'left'} className={classes.errorMessage}>
                {error}
              </Typography>
            </Box>
            <Grid
              className={classes.buttons}
              container
              justifyContent="flex-end"
              spacing={3}
              mt={2}
            >
              <Grid item className={classes.buttonGrid}>
                <HighlightButton
                  className={classes.resetButton}
                  data-testid={'submit_button'}
                  disabled={!newPassword || error !== null}
                  type="submit"
                  variant="contained"
                  onClick={onSubmit}
                  sx={{
                    '&.Mui-disabled': {
                      background: '#BDBDBD',
                      color: '#F2F2F2'
                    }
                  }}
                >
                  {t('forgot_password_code.reset_password')}
                </HighlightButton>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
    </Grid>
  </Grid>
</Grid>
  return (
    <LogInContainer>
      {content}
    </LogInContainer>
  )
}

export default ForgotPasswordCode
